import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Table, Card, Button, Spinner, Toast, Modal } from 'react-bootstrap';
import { Receipt, FileText, Calendar, CreditCard, CheckCircle2, Download, XCircle, FileCheck2, FileCode2 } from 'lucide-react';
import useAuthHeaders from '../hooks/useAuthHeaders';
import './Facturas.css';

const STATUS_LABELS = {
  paid: { label: 'Pagada', className: 'status-paid' },
  open: { label: 'Pendiente', className: 'status-open' },
  void: { label: 'Anulada', className: 'status-void' },
  uncollectible: { label: 'Incobrable', className: 'status-void' },
};

const Facturas = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastVariant, setToastVariant] = useState('danger');
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [downloadUrls, setDownloadUrls] = useState({});
  const [downloadingKey, setDownloadingKey] = useState(null);
  const { userId, getToken } = useAuthHeaders();

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const token = await getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/Stripe/invoices`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        if (response.ok) {
          const data = await response.json();
          setInvoices(data);
        } else {
          throw new Error('Error al obtener las facturas');
        }
      } catch (error) {
        console.error('Error fetching invoices:', error);
        setToastVariant('danger');
        setToastMessage(error.message);
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchInvoices();
    }
  }, [userId, getToken]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatAmount = (amount, currency) => {
    return (amount || 0).toLocaleString('es-MX', {
      style: 'currency',
      currency: currency ? currency.toUpperCase() : 'MXN'
    });
  };

  const handleConfirmGenerate = async () => {
    if (!confirmTarget) return;
    const { stripeInvoiceId } = confirmTarget;

    setGeneratingId(stripeInvoiceId);
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Stripe/invoices/${stripeInvoiceId}/factura`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (data.error === 'FISCAL_DATA_INCOMPLETE') {
          setToastVariant('danger');
          setToastMessage(
            <>
              {data.message || 'Completa tus datos fiscales antes de generar tu factura.'}{' '}
              <Link to="/datos-fiscales" className="toast-link">Ir a Datos Fiscales</Link>
            </>
          );
          setShowToast(true);
          setConfirmTarget(null);
          return;
        }
        throw new Error(data.message || 'No se pudo generar la factura.');
      }

      setInvoices(prev => prev.map(inv =>
        inv.stripeInvoiceId === stripeInvoiceId ? { ...inv, cfdiStatus: 'Emitida' } : inv
      ));
      setToastVariant('success');
      setToastMessage('Factura generada correctamente.');
      setShowToast(true);
      setConfirmTarget(null);
    } catch (error) {
      console.error('Error generating factura:', error);
      setToastVariant('danger');
      setToastMessage(error.message);
      setShowToast(true);
      setConfirmTarget(null);
    } finally {
      setGeneratingId(null);
    }
  };

  const handleDownloadFactura = async (invoice, kind) => {
    const key = `${invoice.stripeInvoiceId}-${kind}`;
    const cached = downloadUrls[invoice.stripeInvoiceId];
    const cachedUrl = cached && (kind === 'pdf' ? cached.pdfUrl : cached.xmlUrl);
    if (cachedUrl) {
      window.open(cachedUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    setDownloadingKey(key);
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Stripe/invoices/${invoice.stripeInvoiceId}/factura/download`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (!response.ok) throw new Error('No se pudo obtener el link de descarga.');

      const data = await response.json();
      setDownloadUrls(prev => ({ ...prev, [invoice.stripeInvoiceId]: data }));

      const url = kind === 'pdf' ? data.pdfUrl : data.xmlUrl;
      if (!url) throw new Error('El archivo no está disponible.');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error downloading factura:', error);
      setToastVariant('danger');
      setToastMessage(error.message);
      setShowToast(true);
    } finally {
      setDownloadingKey(null);
    }
  };

  return (
    <div className="facturas-container">
      <div className="page-header mb-4">
        <div>
          <h2 className="page-title">Mis Facturas</h2>
          <p className="page-subtitle text-muted">Consulta y descarga tus facturas generadas</p>
        </div>
      </div>

      <Card className="facturas-table-card border-0 shadow-sm">
        <Card.Body className="p-4">
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" role="status" style={{ color: '#13a0ff' }}>
                <span className="visually-hidden">Cargando...</span>
              </Spinner>
              <p className="mt-3 text-muted">Cargando facturas...</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="empty-state text-center p-5">
              <Receipt size={64} className="mb-3" style={{ color: '#13a0ff', opacity: 0.3 }} />
              <h5 className="text-muted">No tienes facturas generadas</h5>
              <p className="text-muted">Cuando se genere una factura, aparecerá aquí</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="modern-table">
                <thead>
                  <tr>
                    <th><FileText size={16} className="me-2" />Número</th>
                    <th><Calendar size={16} className="me-2" />Fecha</th>
                    <th><CreditCard size={16} className="me-2" />Monto</th>
                    <th><CheckCircle2 size={16} className="me-2" />Estado</th>
                    <th>Descargar</th>
                    <th>Factura SAT</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice, index) => {
                    const statusInfo = STATUS_LABELS[invoice.status] || { label: invoice.status, className: 'status-open' };
                    const isGenerating = generatingId === invoice.stripeInvoiceId;
                    return (
                      <tr key={invoice.stripeInvoiceId || invoice.invoiceNumber || index}>
                        <td className="invoice-cell">{invoice.invoiceNumber || 'N/A'}</td>
                        <td>{formatDate(invoice.paidAtUtc || invoice.createdAtUtc)}</td>
                        <td>{formatAmount(invoice.amount, invoice.currency)}</td>
                        <td>
                          <span className={`status-badge ${statusInfo.className}`}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td>
                          {invoice.invoicePdfUrl ? (
                            <Button
                              as="a"
                              href={invoice.invoicePdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="outline-primary"
                              size="sm"
                            >
                              <Download size={16} className="me-1" />
                              Descargar
                            </Button>
                          ) : (
                            <Button variant="outline-secondary" size="sm" disabled>
                              No disponible
                            </Button>
                          )}
                        </td>
                        <td>
                          {invoice.amount > 0 ? (
                            invoice.cfdiStatus === 'Emitida' ? (
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  disabled={downloadingKey === `${invoice.stripeInvoiceId}-pdf`}
                                  onClick={() => handleDownloadFactura(invoice, 'pdf')}
                                >
                                  <FileCheck2 size={16} className="me-1" />
                                  PDF
                                </Button>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  disabled={downloadingKey === `${invoice.stripeInvoiceId}-xml`}
                                  onClick={() => handleDownloadFactura(invoice, 'xml')}
                                >
                                  <FileCode2 size={16} className="me-1" />
                                  XML
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={isGenerating}
                                onClick={() => setConfirmTarget(invoice)}
                              >
                                {isGenerating ? (
                                  <Spinner animation="border" size="sm" className="me-1" />
                                ) : (
                                  <Receipt size={16} className="me-1" />
                                )}
                                Generar factura
                              </Button>
                            )
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!confirmTarget} onHide={() => !generatingId && setConfirmTarget(null)} centered>
        <Modal.Header closeButton={!generatingId} className="border-0 pb-0">
          <Modal.Title>
            <div className="d-flex align-items-center">
              <div className="modal-icon-wrapper me-3">
                <Receipt size={24} />
              </div>
              <div>
                <h5 className="mb-0">Generar factura SAT</h5>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {confirmTarget && (
            <>
              <p>
                Estás a punto de emitir tu factura (CFDI) ante el SAT por el pago de{' '}
                <strong>{formatAmount(confirmTarget.amount, confirmTarget.currency)}</strong> del{' '}
                <strong>{formatDate(confirmTarget.paidAtUtc || confirmTarget.createdAtUtc)}</strong>.
              </p>
              <p className="text-muted mb-0">
                Esta acción es <strong>irreversible</strong>: una vez timbrada, la factura queda registrada ante el SAT y no se puede cancelar desde aquí.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="outline-secondary" onClick={() => setConfirmTarget(null)} disabled={!!generatingId}>
            Cancelar
          </Button>
          <Button
            className="btn-gradient"
            onClick={handleConfirmGenerate}
            disabled={!!generatingId}
          >
            {generatingId ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Generando...
              </>
            ) : (
              <>
                <CheckCircle2 size={18} className="me-2" />
                Confirmar y generar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={6000}
        autohide
        className={`custom-toast toast-${toastVariant}`}
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          minWidth: '320px',
          zIndex: 9999
        }}
      >
        <Toast.Header closeButton={false} className="border-0">
          <div className="d-flex align-items-center w-100">
            {toastVariant === 'success' ? (
              <CheckCircle2 size={20} className="me-2" />
            ) : (
              <XCircle size={20} className="me-2" />
            )}
            <strong className="me-auto">{toastVariant === 'success' ? 'Éxito' : 'Error'}</strong>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setShowToast(false)}
            ></button>
          </div>
        </Toast.Header>
        <Toast.Body>
          {toastMessage}
        </Toast.Body>
      </Toast>
    </div>
  );
};

export default Facturas;
